import React, { useState } from 'react';
import { Save, Upload, Check, X } from 'lucide-react';

interface PhoneConfig {
  name: string;
  price: number;
  color: string;
  material: string;
  buttonColor: string;
  hasButtons: boolean;
  screenBorderColor: string;
  cameraStyle: string;
  cameraColor: string;
  cameraPosition: { x: number; y: number };
  buttonPositions: { volume: number; power: number };
  phoneSize: { width: number; height: number; depth: number };
  screenSize: { width: number; height: number };
  cornerRadius: number;
  cpu: string;
  gpu: string;
  ram: string;
  storage: string;
  network: string;
}

interface ConfigManagerProps {
  config: PhoneConfig;
  onConfigLoad: (config: PhoneConfig) => void;
}

export function ConfigManager({ config, onConfigLoad }: ConfigManagerProps) {
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [importStatus, setImportStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleExport = () => {
    try {
      const configString = JSON.stringify(config, null, 2);
      const blob = new Blob([configString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.name.toLowerCase().replace(/\s+/g, '-')}-config.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setExportStatus('success');
      setTimeout(() => setExportStatus('idle'), 2000);
    } catch {
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 2000);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target?.result as string);
          onConfigLoad(config);
          setImportStatus('success');
          setTimeout(() => setImportStatus('idle'), 2000);
        } catch (error) {
          console.error('Error parsing configuration file:', error);
          setImportStatus('error');
          setTimeout(() => setImportStatus('idle'), 2000);
        }
      };
      reader.readAsText(file);
    }
  };

  const getButtonClasses = (status: 'idle' | 'success' | 'error') => {
    const baseClasses = "flex items-center gap-2 px-4 py-2 rounded-lg shadow-md transition-all duration-300";
    switch (status) {
      case 'success':
        return `${baseClasses} bg-theme-success text-theme-bg-primary`;
      case 'error':
        return `${baseClasses} bg-theme-error text-theme-bg-primary`;
      default:
        return `${baseClasses} bg-theme-bg-content hover:bg-theme-bg-secondary`;
    }
  };

  const getStatusIcon = (status: 'idle' | 'success' | 'error', defaultIcon: React.ReactNode) => {
    switch (status) {
      case 'success':
        return <Check className="w-4 h-4" />;
      case 'error':
        return <X className="w-4 h-4" />;
      default:
        return defaultIcon;
    }
  };

  return (
    <div className="absolute top-4 left-4 flex flex-col gap-2">
      <button
        onClick={handleExport}
        className={getButtonClasses(exportStatus)}
      >
        {getStatusIcon(exportStatus, <Save className="w-4 h-4 text-theme-accent" />)}
        <span className={`text-sm ${exportStatus === 'idle' ? 'text-theme-text-secondary' : 'text-white'}`}>
          {exportStatus === 'success' ? 'Exported!' : 
           exportStatus === 'error' ? 'Error!' : 
           'Export Config'}
        </span>
      </button>
      
      <label className={getButtonClasses(importStatus) + " cursor-pointer"}>
        {getStatusIcon(importStatus, <Upload className="w-4 h-4 text-theme-accent" />)}
        <span className={`text-sm ${importStatus === 'idle' ? 'text-theme-text-secondary' : 'text-white'}`}>
          {importStatus === 'success' ? 'Imported!' : 
           importStatus === 'error' ? 'Error!' : 
           'Import Config'}
        </span>
        <input
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />
      </label>
    </div>
  );
}